import { Component, Output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiUser, UsernamesApi, UsersApi } from '@tytapp/api';
import { LoggerService, sleep } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { Subject, Subscription } from 'rxjs';

@Component({
    selector: `tyt-change-username`,
    templateUrl: './change-username.component.html',
    styleUrls: ['./change-username.component.scss']
})
export class ChangeUsernameComponent {
    private userService = inject(UserService);
    private userApi = inject(UsersApi);
    private usernameApi = inject(UsernamesApi);
    private logger = inject(LoggerService);

    submitting = false;
    subscriptions = new Subscription();
    user: ApiUser;
    username: string;
    usernameError: string;
    genericErrorMessage = 'An unknown error occurred. Please try again, if this issue persists contact support@tytnetwork.com';

    usernameAvailable: boolean = null;
    checkingIfUsernameAvailable = false;
    usernameAvailableTimeout;
    lastCheckedUsername: string;
    showUsernameMessage = false;

    @Output() usernameSet = new Subject<void>();
    @Output() canceled = new Subject<void>();

    ngOnInit() {
        this.subscriptions.add(this.userService.userChanged.subscribe(user => this.user = user));
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    async checkIfUsernameAvailable() {
        await sleep(100);
        if (this.lastCheckedUsername === this.username) {
            this.logger.info(`Username '${this.lastCheckedUsername}' has not changed since last check.`);
            return;
        }

        this.logger.info(`Username '${this.username}' is different than last checked username '${this.lastCheckedUsername}'`);
        this.lastCheckedUsername = this.username;

        this.checkingIfUsernameAvailable = true;
        this.showUsernameMessage = true;

        if (!this.canSubmit)
            return;
        clearTimeout(this.usernameAvailableTimeout);

        this.usernameAvailableTimeout = setTimeout(async () => {
            let available = false;
            let usernameToCheck: string = this.username;

            if (this.isValidUsername(usernameToCheck)) {
                try {
                    this.logger.info(`Checking availablity of username '${usernameToCheck}'`);
                    await this.usernameApi.checkUsernameAvailability(usernameToCheck).toPromise();
                    available = true;
                } catch (e) {
                    available = false;
                }
            } else {
                available = false;
            }

            if (this.username !== usernameToCheck)
                return;

            this.usernameAvailable = available;
            this.checkingIfUsernameAvailable = false;
        }, 1000);
    }

    isValidUsername(username: string) {
        return !!username && /^[a-z][a-z0-9_]+$/.test(username) && username.length >= 3;
    }
    canSubmit() {
        return this.isValidUsername(this.username);
    }
    async submit() {
        if (!this.canSubmit() || !this.usernameAvailable)
            return;

        if (!confirm(`Are you sure you want the username @${this.username}? You won't be able to change it later.`))
            return;

        try {
            await this.userApi.updateUser({ username: this.username }).toPromise();
            this.userService.locallyUpdateUser(await this.userApi.getCurrentUser().toPromise());
            this.usernameSet.next();
        } catch (e) {
            if (e.json)
                e = e.json();
            alert(e.message ?? `A problem occurred, please try again later.`);
        }
    }
}